import { useRouterState } from "@tanstack/react-router";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";

import { markTourSeen, shouldAutoStartTour } from "../../help/onboarding-state.js";
import { getPageGuide, type PageGuide } from "../../help/page-guide.js";
import { HelpPanel } from "../layout/help-panel.js";

import { OnboardingTour } from "./onboarding-tour.js";

interface HelpContextValue {
  /** Current route path. */
  currentRoute: string;
  /** Guide for the current route, if any. */
  guide: PageGuide | null;
  /** Open the Help drawer. */
  openHelp: () => void;
  /** (Re)start the first-run walkthrough for the current page. */
  startTour: () => void;
}

const HelpContext = createContext<HelpContextValue | null>(null);

export function useHelp(): HelpContextValue {
  const ctx = useContext(HelpContext);
  if (!ctx) throw new Error("useHelp must be used within <HelpProvider>");
  return ctx;
}

/**
 * Owns the cross-page help surfaces — the Help drawer and the onboarding tour —
 * and exposes `openHelp` / `startTour` to any page. Auto-opens the first-run
 * tour the first time a guided page is visited (or when its guide version
 * bumps); marks it seen on completion or skip so it doesn't reappear.
 */
export function HelpProvider({ children }: { children: ReactNode }) {
  const { location } = useRouterState();
  const currentRoute = location.pathname;
  const guide = useMemo(() => getPageGuide(currentRoute), [currentRoute]);
  const [helpOpen, setHelpOpen] = useState(false);
  // Tour open-ness is *derived*, not set in an effect: it auto-opens on a
  // guided route the user hasn't seen (or whose version bumped), unless they've
  // closed it this session; `startTour` opens it manually.
  const [manualTourRoute, setManualTourRoute] = useState<string | null>(null);
  const [closedTourRoutes, setClosedTourRoutes] = useState<string[]>([]);

  const autoEligible = guide ? shouldAutoStartTour(guide.route, guide.version) : false;
  const tourOpen =
    guide !== null &&
    (manualTourRoute === currentRoute ||
      (autoEligible && !closedTourRoutes.includes(currentRoute)));

  const openHelp = useCallback(() => setHelpOpen(true), []);
  const startTour = useCallback(() => setManualTourRoute(currentRoute), [currentRoute]);

  const finishTour = useCallback(() => {
    if (guide) markTourSeen(guide.route, guide.version);
    setManualTourRoute((route) => (route === currentRoute ? null : route));
    setClosedTourRoutes((routes) =>
      routes.includes(currentRoute) ? routes : [...routes, currentRoute],
    );
  }, [guide, currentRoute]);

  const value = useMemo<HelpContextValue>(
    () => ({ currentRoute, guide, openHelp, startTour }),
    [currentRoute, guide, openHelp, startTour],
  );

  return (
    <HelpContext.Provider value={value}>
      {children}
      <HelpPanel
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        currentRoute={currentRoute}
        guide={guide}
        onReplayTour={startTour}
      />
      {guide && (
        <OnboardingTour
          // Remount per route + open so the walkthrough always starts at step 1.
          key={`${currentRoute}:${tourOpen}`}
          guide={guide}
          open={tourOpen}
          onClose={finishTour}
          onComplete={finishTour}
        />
      )}
    </HelpContext.Provider>
  );
}
